/**
 * Get Blog Posts API
 *
 * Fetch blog posts from Supabase with optional filtering.
 * @see src/features/blog/hooks/useBlogPosts.ts for React Query wrapper
 */

import { supabase } from '@/lib/supabase';
import type { BlogPost } from '../types';

/**
 * Fetch all blog posts from the database
 * @returns Promise resolving to array of BlogPost objects
 */
export const fetchBlogPosts = async (): Promise<BlogPost[]> => {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

/**
 * Fetch a single blog post by ID
 * @param id - The blog post ID
 * @returns Promise resolving to a BlogPost object
 */
export const fetchBlogPostById = async (id: string): Promise<BlogPost> => {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Fetch a single blog post by slug
 * @param slug - The blog post slug
 * @returns Promise resolving to a BlogPost object
 */
export const fetchBlogPostBySlug = async (slug: string): Promise<BlogPost> => {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Search blog posts by query string
 * Used by global search feature
 * @param query - Search query string
 * @param limit - Maximum number of results (default: 10)
 * @returns Promise resolving to array of BlogPost objects
 */
export const searchBlogPosts = async (query: string, limit = 10): Promise<BlogPost[]> => {
  if (!query.trim()) return [];

  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .or(`title.ilike.%${query}%,excerpt.ilike.%${query}%,content.ilike.%${query}%`)
    .limit(limit);

  if (error) throw error;
  return data || [];
};
